/**
 * Bepaalt of een sollicitatie effectief in de «Voorgesteld»-kolom hoort (Kanban)
 * óf in de lijst «voorgesteld voor klantportaal» (vacature-tab), los van ruwe DB-status «New»/Shortlist.
 */

import { BULLHORN_STATUS_MAP } from '../types';
import { normalizeCvSnapshotForPortal } from './portalProposalThread';

function portalProposalPublished(cv: unknown): boolean {
  const snap = normalizeCvSnapshotForPortal(cv);
  if (!Object.keys(snap).length) return false;
  const p = snap.portal_client_proposal;
  if (!p || typeof p !== 'object' || Array.isArray(p)) return false;
  return !!(p as Record<string, unknown>).published_at;
}

function voorgesteldPipelineMeta(cv: unknown): boolean {
  const snap = normalizeCvSnapshotForPortal(cv);
  if (!Object.keys(snap).length) return false;
  const m = snap.pipeline_meta as Record<string, unknown> | undefined;
  if (!m || typeof m !== 'object') return false;
  const d = m.voorgesteld_date;
  return typeof d === 'string' && d.trim().length > 0;
}

/** Ruwe `applications.status` — vroege fase zonder expliciete voorgesteld-markering. */
export function isEarlyPipelineDbStatus(status: string | null | undefined): boolean {
  const s = String(status || '').trim().toLowerCase();
  if (!s) return true;
  return (
    ['new', 'nieuw', 'lead', 'shortlist', 'op shortlist', 'screening', 'applied', 'submitted'].includes(s) ||
    s.startsWith('status_new')
  );
}

/** Toon in Voorgesteld-kolom / voorgesteld-sectie: echte status, publicatie, of voorgesteld-datum in snapshot. */
export function isEffectiveVoorgesteldStage(app: { status?: string; cv_snapshot?: unknown } | null | undefined): boolean {
  if (!app) return false;
  const st = String(app.status || '');
  if (/voorgesteld|voorgedragen|voorstel/i.test(st)) return true;
  if (portalProposalPublished(app.cv_snapshot)) return true;
  if (voorgesteldPipelineMeta(app.cv_snapshot)) return true;
  return false;
}

/**
 * Als de database-status al een later vak in de standaard-pipeline is (Gesprek, Aanbod, …), mag die
 * sollicitatie niet óók in de «Voorgesteld»-kolom blijven staan op basis van portal-publicatie of
 * `voorgesteld_date` — anders dubbele kaarten in Kanban.
 */
export function dbStatusOverridesVoorgesteldKanbanBucket(status: string | null | undefined): boolean {
  const raw = String(status || '').trim();
  if (!raw) return false;
  const mapped = BULLHORN_STATUS_MAP[raw] ? String(BULLHORN_STATUS_MAP[raw]).trim() : '';
  for (const s of [raw, mapped].filter(Boolean)) {
    const lower = s.toLowerCase();
    // Klantportaal: expliciete afwijzing door opdrachtgever hoort niet meer in «Voorgesteld» (voorkomt dubbele kaarten).
    if (/afgewezen\s+door\s+opdrachtgever/i.test(lower)) return true;
    if (['gesprek', 'aanbod', 'plaatsing', 'afgewezen'].includes(lower)) return true;
    if (['Gesprek', 'Aanbod', 'Plaatsing', 'Afgewezen'].includes(s)) return true;
    if (/\b(aangenomen|geplaatst|hired|placed|gunning)\b/i.test(lower)) return true;
  }
  return false;
}

/** Laatste regel uit `cv_snapshot.portal_client_feedback` (alleen berichten van de klant). */
export function getLastPortalClientFeedback(cv: unknown): { text: string; at?: string } | null {
  try {
    const snap = normalizeCvSnapshotForPortal(cv);
    const arr = snap.portal_client_feedback;
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const last = arr[arr.length - 1] as { at?: string; text?: string };
    const text = typeof last?.text === 'string' ? last.text.trim() : '';
    if (!text) return null;
    const at = typeof last?.at === 'string' ? last.at : undefined;
    return { text, at };
  } catch {
    return null;
  }
}

/**
 * Vacature-tab «voorgestelde kandidaten»: niet elke `New`-sollicitatie (die in UI «wacht op feedback» krijgt),
 * alleen dossiers die echt bij voorgesteld / klantportaal horen.
 */
export function includeInVacancyVoorgesteldClientList(
  app: { status?: string; cv_snapshot?: unknown } | null | undefined,
): boolean {
  if (!app) return false;
  if (isEffectiveVoorgesteldStage(app)) return true;
  const s = normalizeCvSnapshotForPortal(app.cv_snapshot);
  if (!Object.keys(s).length) return false;
  if (s.portal_client_cv_formatted && typeof s.portal_client_cv_formatted === 'object') return true;
  if (Array.isArray(s.portal_thread) && s.portal_thread.length > 0) return true;
  const p = s.portal_client_proposal;
  if (p && typeof p === 'object' && !Array.isArray(p)) {
    const o = p as Record<string, unknown>;
    if (o.published_at) return true;
    const sum = String(o.summary || '').trim();
    const mn = String(o.match_notes || '').trim();
    if (sum || mn) return true;
  }
  return false;
}
